import axios from 'axios'
import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'
import aa from 'search-insights'
import { algoliaAppId, algoliaSearchKey } from '../../config/config/settings.json'

// Storefront logic shared with the website (../front/src). Do not fork these here.
import { useStore } from '@front/composables/store'
import { initUser } from '@front/helpers/user'
import initCart from '@front/helpers/cart'
import { syncAuthHeader, installSessionResumeListeners } from '@front/helpers/session'
import { shouldDropSentryEvent } from '@front/helpers/sentry.js'
import formatPhone from '@front/helpers/formatPhone'
import Mango from '@front/helpers/mango'

import App, { clearUndefinedMemberSession } from './App.vue'
import Spinner from './components/Spinner.vue'
import { createAppRouter } from './router'
import { installTheme, theme } from './lib/theme'
import useBreakpoint from '@front/helpers/breakpoints'
import { installKeyboardTracking } from './lib/keyboard'
import { installNative, hideSplash } from './lib/native'
import { installSiteLinks } from './lib/content/website'
import { initPush, linkPushUser, unlinkPushUser } from './lib/push'
import { oneSignalAppId } from './lib/pushConfig'
import './styles/index.css'
import './styles/places-autocomplete.css'

const isDev = process.env.NODE_ENV != 'production'

import { vSafeHtml } from './helpers/safeHtml.js'

const app = createApp(App)
app.directive('safe-html', vSafeHtml)
const pinia = createPinia()
const router = createAppRouter()

app.use(pinia)
app.use(router)

Sentry.init({
	app,
	dsn: 'https://924fdcd938dfb37858e3b33aaf7aaddf@o4506316278595584.ingest.us.sentry.io/4510796279906304',
	enabled: !isDev,
	environment: isDev ? 'development' : 'production',
	release: __SENTRY_RELEASE__ || undefined,
	initialScope: { tags: { app: 'mobile' } },
	integrations: [Sentry.browserTracingIntegration({ router })],
	tracesSampleRate: isDev ? 1.0 : 0.2,
	tracePropagationTargets: ['localhost', /^https:\/\/api\.inhishands\.com/],
	sendDefaultPii: true,
	beforeSend(event) {
		if (shouldDropSentryEvent(event)) return null
		return event
	},
})

aa('init', { apiKey: algoliaSearchKey, appId: algoliaAppId, useCookie: true })

installSiteLinks(router)
installNative()
installTheme()
installKeyboardTracking()

// Same start-up as the website: restore the member, then their cart.
const store = useStore()

store.initUserCart = async () => {
	store.user = await initUser()
	const cart = await initCart(store.user, store.api)
	store.cart = cart.cart
	store.itemsCount = cart.itemsCount
	store.certificatesCount = cart.certificatesCount
	store.cartTotalItems = cart.cartTotalItems
	store.cartSubtotal = cart.cartSubtotal
	store.texasStateSalesTax = cart.texasStateSalesTax
	store.originalCart = cart.originalCart
	store.cartTotal = cart.cartTotal
	store.saveCart = async () => {
		await cart.saveCart()
	}
	store.syncOriginalCart = () => {
		cart.syncOriginalCart()
	}
}

// The router waits on this before sending a saved session to the sign-in page.
// Clear a stored "undefined" member id before the member request.
clearUndefinedMemberSession()
store.userReady = store
	.initUserCart()
	.catch((error) => {
		Sentry.captureException(error)
	})
	.finally(() => {
		store.cartInitialized = true
	})

// Categories and brands feed the Shop tab, search and the category pages.
store.loadCatalog = async () => {
	if (store.categories?.length) return store.categories
	const categories = await Mango.categories({
		search: { isVisible: true, treeId: { $lte: 1 } },
		limit: 999,
		fields: ['name', 'url', 'imageUrl', 'parentId', 'bcId', 'sortOrder', 'description', 'filters', 'defaultProductSort'],
		sort: { sortOrder: 1 },
	})
	for (const c of categories) c.subcategories = categories.filter((sc) => sc.parentId == c.bcId)
	store.categories = categories
	return categories
}
store.loadCatalog().catch((error) => Sentry.captureException(error))

store.loadBrands = async () => {
	if (store.brands?.length) return store.brands
	store.brands = await Mango.brands({ limit: 999, fields: ['id', 'bcId', 'customUrl', 'name', 'imageUrl', 'metaDescription', 'pageTitle'] })
	return store.brands
}

syncAuthHeader(axios)
installSessionResumeListeners({ store, axios })

app.provide('store', store)
app.provide('axios', axios)
app.provide('formatPhone', formatPhone)
app.provide('mode', import.meta.env.MODE)
// Website components mounted in the app (info pages, wallet buttons) may inject these.
app.provide('breakpoints', useBreakpoint().breakpoints)
app.provide('darkMode', {
	get enabled() {
		return theme.dark
	},
	toggle() {},
})

// The website's payment buttons (mounted in checkout) use <Spinner> without importing it.
app.component('Spinner', Spinner)

app.mount('#app')
router.isReady().finally(hideSplash)

// Push is a no-op outside the native shell. The phone follows whoever is signed in: sign-in and
// session restore link the member, and any way of losing the session (sign-out, expired or
// rejected token) unlinks it.
initPush(oneSignalAppId)
watch(
	() => store.user?.id,
	(id) => (id ? linkPushUser(id) : unlinkPushUser()),
	{ immediate: true },
)
